import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { COURSE_CATALOG_NAMES } from '../constants/coursesCatalog';
import { COURSE_CONFIG } from '../types';
import { queryCoursesRows } from './coursesService';

let inflight: Promise<void> | null = null;

function isSchemaColumnError(err: { code?: string } | null): boolean {
  const c = String(err?.code ?? '');
  return c === '42703' || c === 'PGRST204';
}

/**
 * Ensures every name in `COURSE_CATALOG_NAMES` exists as a row in `courses` for the default tenant.
 * Inserts only missing rows; does not overwrite prices or other fields on existing courses.
 * Works when `courses.tenant_id` is missing (older DBs): falls back to unscoped select/insert.
 */
export function syncCatalogCoursesToDb(): Promise<void> {
  if (!isSupabaseConfigured()) return Promise.resolve();
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // Include inactive rows so a deactivated course is not re-inserted on every login.
      const existingRows = await queryCoursesRows();
      const have = new Set(
        existingRows.map((r) => String((r as { name?: string }).name || '').trim().toLowerCase()).filter(Boolean)
      );
      const missing = COURSE_CATALOG_NAMES.filter((name) => !have.has(name.trim().toLowerCase()));
      if (missing.length === 0) return;

      const payloads: Record<string, unknown>[][] = missing.map((name) => {
        const cfg = COURSE_CONFIG[name as keyof typeof COURSE_CONFIG];
        const validity_months = cfg?.validityMonths ?? 36;
        const full = {
          tenant_id: DEFAULT_TENANT_ID,
          name,
          base_price: 0,
          vat_rate: 23,
          validity_months,
          is_active: true,
        };
        const { tenant_id: _t, ...noTenant } = full;
        const { vat_rate: _v, ...noVat } = noTenant;
        return [full, noTenant, noVat, { name, base_price: 0, validity_months, is_active: true }, { name, is_active: true }];
      });

      // Zip parallel arrays: same missing index across payload variants
      const variants = [0, 1, 2, 3, 4].map((vi) => payloads.map((p) => p[vi]));

      let lastError: { code?: string; message?: string } | null = null;
      for (const batch of variants) {
        const ins = await supabase.from('courses').insert(batch);
        lastError = ins.error;
        if (!ins.error) break;
        if (ins.error.code === '23505') {
          lastError = null;
          break;
        }
        if (!isSchemaColumnError(ins.error)) break;
      }
      if (lastError && lastError.code !== '23505') throw lastError;
    } catch (e) {
      console.warn('[coursesCatalogSync]', e);
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
